import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { AddressDrawer } from './AddressDrawer';
import { useAddressForm } from './useAddressForm';
import type { AddressUpdateResponse } from './types';
import type { FrontOverlayMode } from '../../../ips-react-front-fw/components/overlay';
import { registerDynamicBootstrap } from '../../shared/registerDynamicBootstrap';
import './address.css';

const mountSelector = '#ips-react-address-root';
type AddressType = 'billing' | 'shipping';
type AddressEditorMode = FrontOverlayMode;

const isAddressType = (value: unknown): value is AddressType =>
  value === 'billing' || value === 'shipping';

const isAddressEditorMode = (value: unknown): value is AddressEditorMode =>
  value === 'drawer' || value === 'modal';

const readAddressFromUrl = (): AddressType | null => {
  const params = new URL(window.location.href).searchParams;
  const value = params.get('address');
  return isAddressType(value) ? value : null;
};

const writeAddressToUrl = (type: AddressType | null): void => {
  const url = new URL(window.location.href);
  if (type) {
    url.searchParams.set('address', type);
  } else {
    url.searchParams.delete('address');
  }

  const nextHref = `${url.pathname}${url.search}${url.hash}`;
  const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (nextHref !== currentHref) {
    window.history.pushState({ ipsAddress: type }, '', nextHref);
  }
};

const updateAddressCardContent = (type: AddressType, formatted?: string): void => {
  const content = document.querySelector<HTMLElement>(`[data-ips-address-content="${type}"]`);
  const body = document.querySelector<HTMLElement>(`[data-ips-address-body="${type}"]`);
  if (!content || !body) return;

  const html = (formatted || '').trim();
  const fallback = body.dataset.ipsEmptyText || '';
  content.innerHTML = html !== '' ? html : fallback;
};

const bootstrap = () => {
  const mount = document.querySelector<HTMLElement>(mountSelector);
  if (!mount || mount.dataset.ipsReactMounted === '1') {
    return;
  }

  const endpoint = mount.dataset.endpoint || '';
  const nonce = mount.dataset.nonce || '';
  if (!endpoint || !nonce) {
    return;
  }

  mount.dataset.ipsReactMounted = '1';

  const root = createRoot(mount);
  const editorMode: AddressEditorMode = isAddressEditorMode(mount.dataset.editorMode)
    ? mount.dataset.editorMode
    : 'drawer';

  const App: React.FC = () => {
      const [open, setOpen] = useState(false);
      const [type, setType] = useState<AddressType>('billing');
      const syncingFromHistory = useRef(false);

      const { payload, values, loading, saving, error, onChange, onSubmit } = useAddressForm(
        endpoint,
        nonce,
        type,
        open
      );

      const title = useMemo(
        () => (type === 'billing' ? 'Rechnungsadresse bearbeiten' : 'Lieferadresse bearbeiten'),
        [type]
      );

      const openDrawer = useCallback((nextType: AddressType, syncUrl = true) => {
        setType(nextType);
        setOpen(true);
        if (syncUrl) {
          writeAddressToUrl(nextType);
        }
      }, []);

      const closeDrawer = useCallback((syncUrl = true) => {
        setOpen(false);
        if (syncUrl) {
          writeAddressToUrl(null);
        }
      }, []);

      useEffect(() => {
        const handler = (e: Event) => {
          const target = (e.target as HTMLElement)?.closest<HTMLElement>('[data-ips-address]');
          if (!target) return;
          const nextType = target.dataset.ipsAddress;
          if (!isAddressType(nextType)) return;

          e.preventDefault();
          openDrawer(nextType);
        };

        document.addEventListener('click', handler, true);
        return () => {
          document.removeEventListener('click', handler, true);
        };
      }, [openDrawer]);

      useEffect(() => {
        const fromUrl = readAddressFromUrl();
        if (fromUrl) {
          openDrawer(fromUrl, false);
        }
      }, [openDrawer]);

      useEffect(() => {
        const onPopState = () => {
          syncingFromHistory.current = true;
          const fromUrl = readAddressFromUrl();
          if (fromUrl) {
            openDrawer(fromUrl, false);
          } else {
            closeDrawer(false);
          }
          syncingFromHistory.current = false;
        };

        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
      }, [closeDrawer, openDrawer]);

      const handleSaved = useCallback(
        (result: AddressUpdateResponse | null) => {
          if (!result) return;
          const savedType = isAddressType(result.type) ? result.type : type;
          updateAddressCardContent(savedType, result.formatted);
        },
        [type]
      );

      return (
        <AddressDrawer
          open={open}
          mode={editorMode}
          onClose={() => closeDrawer(!syncingFromHistory.current)}
          title={title}
          fields={payload?.fields ?? null}
          values={values}
          loading={loading}
          saving={saving}
          error={error}
          onChange={onChange}
          onSubmit={onSubmit}
          onSaved={handleSaved}
        />
      );
    };

  root.render(<App />);
};

registerDynamicBootstrap(bootstrap);
